import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { FormInstance, Select } from 'antd';
import { CategorySelect } from '../../../classifier';
import {
  DatePicker,
  DirectoryFilter,
  NoContentItem,
} from '../../../common/components';
import { filterOption } from '../../../common/form';
import { useUnifiedRef } from '../../../common/useUnifiedRef';
import {
  DATE_FORMAT,
  formatCodeName,
  getDayjs,
  getIsoDateString,
} from '../../../common/utils';
import { useSystemClassifierSelectOptions } from '../../../configuration';
import { DataObjectDescriptionVersionStore } from '../../DataObjectDescriptionVersionStore';
import { AlgorithmFilter } from '../query/useGetAlgorithmsQuery';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './AlgorithmsTableFilter.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('algorithms-table');
addTranslations(translations);

export interface AlgorithmsTableFilterProps {
  isOpen?: boolean;
  onClose?: () => void;
  filter?: AlgorithmFilter;
  onFilter?: (filterState: AlgorithmFilter) => void;
}

export const AlgorithmsTableFilter = forwardRef<
  FormInstance,
  AlgorithmsTableFilterProps
>((props, ref) => {
  const internalRef = useRef<FormInstance | null>(null);
  const unifiedRef = useUnifiedRef(ref, internalRef);
  const { isOpen, onClose = noop, filter, onFilter } = props;
  const { t } = useTranslation();

  const [dataObjectDescriptionVersion] =
    DataObjectDescriptionVersionStore.useValue('dataObjectDescriptionVersion');

  const [dimensionOptions, indicatorsOptions] = useMemo(
    () => [
      (dataObjectDescriptionVersion.dimensions || []).map(getOption),
      (dataObjectDescriptionVersion.indicators || []).map(getOption),
    ],
    [dataObjectDescriptionVersion],
  );

  const [selectedDimension, setSelectedDimension] = useState<
    number | undefined
  >(undefined);

  const handleChangeDimension = useCallback(
    (dimensionId: number | undefined) => {
      setSelectedDimension(dimensionId);
      unifiedRef.current?.setFieldValue('dimensionCategoryIds', []);
    },
    [unifiedRef],
  );

  const selectedDimensionClassifierCode = useMemo(
    () =>
      dataObjectDescriptionVersion.dimensions?.find(
        ({ id }) => id === selectedDimension,
      )?.classifier?.classifier.code,
    [selectedDimension, dataObjectDescriptionVersion.dimensions],
  );

  const handleClearDrawerFilters = useCallback(() => {
    onFilter?.({});
    unifiedRef.current?.setFieldsValue({
      statusCodes: undefined,
      validPeriod: undefined,
      dimensionIds: undefined,
      dimensionCategoryIds: undefined,
      indicatorIds: undefined,
    });
  }, [onFilter, unifiedRef]);

  const handleDrawerFiltersChange = useCallback(
    ({
      validPeriod,
      statusCodes,
      ...rest
    }: Omit<AlgorithmFilter, 'validFrom' | 'validTo'> & {
      validPeriod?: [Date | null, Date | null] | null;
    }) =>
      onFilter?.({
        ...filter,
        ...rest,
        validFrom: validPeriod?.[0]
          ? getIsoDateString(validPeriod?.[0])
          : undefined,
        validTo: validPeriod?.[1]
          ? getIsoDateString(validPeriod?.[1])
          : undefined,
        statusCodes: statusCodes?.length ? statusCodes : undefined,
      }),
    [onFilter, filter],
  );

  const systemClassifierOptionsByCode = useSystemClassifierSelectOptions(
    ['ALGORITHM_STATUS'],
    'code',
  );

  if (!isOpen) {
    return null;
  }

  return (
    <DirectoryFilter
      ref={unifiedRef.ref}
      onClear={handleClearDrawerFilters}
      onClose={onClose}
      onFilter={handleDrawerFiltersChange}
      fields={[
        {
          name: 'statusCodes',
          label: t('status.label'),
          initialValue: filter?.statusCodes,
          input: () => (
            <Select
              id="status-filter"
              placeholder={t('status.placeholder')}
              mode="multiple"
              allowClear
              options={systemClassifierOptionsByCode.ALGORITHM_STATUS}
              filterOption={filterOption}
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
            />
          ),
        },
        {
          name: 'validPeriod',
          label: t(`validPeriod.label`),
          initialValue: filter
            ? [
                filter.validFrom ? getDayjs(filter.validFrom) : null,
                filter.validTo ? getDayjs(filter.validTo) : null,
              ]
            : undefined,
          input: () => (
            <DatePicker.RangePicker
              id="validPeriod-filter"
              allowEmpty={[true, true]}
              format={DATE_FORMAT}
              allowClear
            />
          ),
        },
        {
          name: 'dimensionIds',
          label: t(`dimension.label`),
          initialValue: filter?.dimensionIds,
          input: () => (
            <Select
              id="dimension-filter"
              placeholder={t('dimension.placeholder')}
              allowClear
              options={dimensionOptions}
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
              notFoundContent={<NoContentItem />}
              onChange={handleChangeDimension}
            />
          ),
        },
        {
          name: 'dimensionCategoryIds',
          label: t(`dimensionCategory.label`),
          initialValue: filter?.dimensionCategoryIds,
          input: () => (
            <CategorySelect
              id="dimension-filter"
              allowClear
              code={selectedDimensionClassifierCode}
              filterOption={filterOption}
            />
          ),
        },
        {
          name: 'indicatorIds',
          label: t(`indicator.label`),
          initialValue: filter?.indicatorIds,
          input: () => (
            <Select
              id="dimension-filter"
              placeholder={t('indicator.placeholder')}
              mode="multiple"
              allowClear
              options={indicatorsOptions}
              filterOption={filterOption}
              styles={{
                popup: { root: { width: 'max-content' } },
              }}
              notFoundContent={<NoContentItem />}
            />
          ),
        },
      ]}
    />
  );
});

const getOption = ({
  code,
  name,
  id,
}: {
  code: string;
  name: string;
  id?: number | null;
}) => ({
  label: formatCodeName({ code, name }),
  value: id,
});

/* eslint-disable @typescript-eslint/no-empty-function */
const noop = () => {};
